import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryString } from "@anyseal/api-client";
import {
  Button,
  Card,
  Drawer,
  Empty,
  Input,
  Result,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from "antd";
import {
  CloseOutlined,
  CopyOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  MetricCard,
  PageHeader,
  Segmented,
  formatDate,
  formatNumber,
} from "@anyseal/ui";
import { portalClient } from "../api";
import { useI18n } from "../i18n";
import { projectPath, useWorkspace } from "../workspace";
import { statusLabel } from "../status";
import { DetailsPanel } from "../DetailsPanel";
import { WorkspaceGuard } from "../WorkspaceGuard";
import { useMinWidth } from "../useMediaQuery";
import { metrics } from "../theme";
import "../styles/usage.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface RelayRequest {
  request_id: string; credential_name: string; protocol: string;
  operation: string; model: string; stream: boolean; status: string;
  input_tokens?: number; output_tokens?: number; charged_microunits?: number;
  started_at: string; completed_at?: string;
  /** Reliability data carried by the portal contract (error classes and
   *  routing internals stay on the admin surface). */
  attempts?: number; upstream_duration_ms?: number;
}
interface RequestPage { items: RelayRequest[]; total: number; limit: number; offset: number; }
interface Stats {
  requests: number; succeeded: number; failed: number;
  pending_reconciliation: number; input_tokens: number;
  output_tokens: number; charged_microunits: number;
}
interface Usage {
  totals: { requests: number; input_tokens: number; output_tokens: number; spend_microunits: number; average_duration_ms: number };
  series: Array<{ bucket: string; requests: number }>;
}
interface Directory { items: Array<{ model_name: string; protocols: string[] }>; }

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDuration(v: number | undefined) {
  if (v === undefined) return "-";
  return v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`;
}
function durationMs(row: RelayRequest) {
  if (!row.completed_at) return undefined;
  const v = Date.parse(row.completed_at) - Date.parse(row.started_at);
  return Number.isFinite(v) && v >= 0 ? v : undefined;
}

type StatusTone = "success" | "error" | "warning" | "default";
function statusTone(status: string): StatusTone {
  const s = status.toLowerCase();
  if (s.includes("success") || s === "succeeded") return "success";
  if (s.includes("fail") || s.startsWith("4") || s.startsWith("5")) return "error";
  if (s.includes("pending") || s.includes("reconcil") || s.includes("processing")) return "warning";
  return "default";
}

// ── Status pill ──────────────────────────────────────────────────────────────

function StatusPill({ status, locale }: { status: string; locale: "zh-CN" | "en" }) {
  const tone = statusTone(status);
  return (
    <Tag className="usage-pill" color={tone}>
      <span className="usage-pill__dot" />
      {statusLabel(status, locale)}
    </Tag>
  );
}

/** Debounce a fast-changing value (search input) before it reaches a query key. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ── Request details body (shared between panel and drawer) ───────────────────

function DetailRow({ label, value, mono }: { label: ReactNode; value: ReactNode; mono?: boolean }) {
  return (
    <div className="usage-detail__row">
      <span className="usage-detail__label">{label}</span>
      <span className={`usage-detail__value${mono ? " usage-detail__value--mono" : ""}`}>{value}</span>
    </div>
  );
}

function DetailSection({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="usage-detail__section">
      <span className="usage-detail__kicker">{title}</span>
      {children}
    </section>
  );
}

/** Status pill + full request ID. The ID wraps instead of truncating —
 *  an ellipsized ID defeats the "trace by request ID" workflow. */
function RequestSummaryHead({ req, locale }: { req: RelayRequest; locale: "zh-CN" | "en" }) {
  return (
    <div className="usage-panel__summary">
      <StatusPill status={req.status} locale={locale} />
      <Typography.Text
        className="usage-panel__reqid"
        copyable={{ text: req.request_id, icon: <CopyOutlined /> }}
      >
        {req.request_id}
      </Typography.Text>
    </div>
  );
}

function RequestDetailsBody({ req, locale, isChinese, currency }: {
  req: RelayRequest; locale: "zh-CN" | "en"; isChinese: boolean; currency: string;
}) {
  const dur = durationMs(req);
  const tokensIn = req.input_tokens ?? 0;
  const tokensOut = req.output_tokens ?? 0;
  const charged = req.charged_microunits;

  // Diagnostics rows render only when the API has a value; the section
  // hides entirely when the request carried no diagnostic detail.
  const diagnostics: Array<{ key: string; label: ReactNode; value: ReactNode; mono?: boolean }> = [
    req.upstream_duration_ms != null
      ? { key: "updur", label: isChinese ? "上游耗时" : "Upstream duration", value: fmtDuration(req.upstream_duration_ms) }
      : null,
    req.attempts != null && req.attempts > 0
      ? { key: "attempts", label: isChinese ? "上游尝试次数" : "Upstream attempts", value: formatNumber(req.attempts, locale) }
      : null,
  ].filter((row): row is NonNullable<typeof row> => row !== null);

  return (
    <div className="usage-panel__body">
      <DetailSection title={isChinese ? "概览" : "Overview"}>
        <DetailRow label={isChinese ? "模型" : "Model"} value={req.model || "—"} />
        <DetailRow label={isChinese ? "凭证" : "Credential"} value={req.credential_name || "—"} />
        <DetailRow label={isChinese ? "协议" : "Protocol"} value={req.protocol || "—"} mono />
        <DetailRow label={isChinese ? "操作" : "Operation"} value={req.operation || "—"} mono />
        <DetailRow label={isChinese ? "流式" : "Streaming"} value={isChinese ? (req.stream ? "是" : "否") : (req.stream ? "Yes" : "No")} />
        <DetailRow label={isChinese ? "开始时间" : "Started"} value={formatDate(req.started_at, locale)} />
        <DetailRow label={isChinese ? "完成时间" : "Completed"} value={req.completed_at ? formatDate(req.completed_at, locale) : "—"} />
        <DetailRow label={isChinese ? "总耗时" : "Total duration"} value={fmtDuration(dur)} />
      </DetailSection>

      <DetailSection title={isChinese ? "用量与费用" : "Usage & charge"}>
        <div className="usage-detail__stats">
          <div className="usage-detail__stat">
            <span>{isChinese ? "输入" : "Input"}</span>
            <strong>{formatNumber(tokensIn, locale)}</strong>
          </div>
          <div className="usage-detail__stat">
            <span>{isChinese ? "输出" : "Output"}</span>
            <strong>{formatNumber(tokensOut, locale)}</strong>
          </div>
          <div className="usage-detail__stat">
            <span>{isChinese ? "合计" : "Total"}</span>
            <strong>{formatNumber(tokensIn + tokensOut, locale)}</strong>
          </div>
        </div>
        <DetailRow
          label={isChinese ? "实际费用" : "Charged"}
          value={charged == null ? (isChinese ? "未产生费用" : "No charge") : `${formatNumber(charged / 1_000_000, locale, { maximumFractionDigits: 4 })} ${currency}`}
        />
      </DetailSection>

      {diagnostics.length > 0 && (
        <DetailSection title={isChinese ? "诊断" : "Diagnostics"}>
          {diagnostics.map((row) => <DetailRow key={row.key} label={row.label} value={row.value} mono={row.mono} />)}
        </DetailSection>
      )}

      <Typography.Text className="usage-panel__note" type="secondary">
        {isChinese ? "不展示 API 密钥、请求头、提示词或响应正文。" : "API keys, headers, prompts, and response bodies are never shown."}
      </Typography.Text>
    </div>
  );
}

// ── UsagePage ────────────────────────────────────────────────────────────────

export function UsagePageInner() {
  const { locale } = useI18n();
  const { organization, project } = useWorkspace();
  const isChinese = locale === "zh-CN";
  const showPanel = useMinWidth(metrics.panelBreakpoint);

  const [requestIdInput, setRequestIdInput] = useState("");
  const requestId = useDebounced(requestIdInput.trim());
  const [status, setStatus] = useState("");
  const [model, setModel] = useState("");
  const [protocol, setProtocol] = useState("");
  const [offset, setOffset] = useState(0);
  const [rangeDays, setRangeDays] = useState<1 | 7 | 30>(30);
  const [selected, setSelected] = useState<RelayRequest>();

  const base = organization && project ? projectPath(organization.id, project.id) : "";
  const range = useMemo(() => {
    const to = new Date();
    return { from: new Date(to.getTime() - rangeDays * 86_400_000).toISOString(), to: to.toISOString() };
  }, [rangeDays]);

  const filters = { ...range, request_id: requestId, status, model, protocol, limit: 50, offset };
  const reqQuery   = useQuery({ queryKey: ["portal", "requests", base, filters], queryFn: () => portalClient.get<RequestPage>(`${base}/usage/requests${queryString(filters)}`), enabled: Boolean(base) });
  const statsQuery = useQuery({ queryKey: ["portal", "request-stats", base, range], queryFn: () => portalClient.get<Stats>(`${base}/usage/stats${queryString(range)}`), enabled: Boolean(base) });
  // Authoritative aggregates for the range: totals and the real daily series.
  const usageQuery = useQuery({ queryKey: ["portal", "usage-analytics", base, range], queryFn: () => portalClient.get<Usage>(`${base}/analytics/usage${queryString({ ...range, bucket: "day" })}`), enabled: Boolean(base) });
  // Filter options come from the model directory, not from the 50 rows on
  // the current page.
  const directory = useQuery({ queryKey: ["portal", "model-directory"], queryFn: () => portalClient.get<Directory>("/api/portal/v1/models/directory") });

  const rows       = reqQuery.data?.items ?? [];
  const totalCount = reqQuery.data?.total ?? 0;
  const reqCount   = statsQuery.data?.requests ?? 0;
  const failed     = statsQuery.data?.failed ?? 0;
  const tokenUsage = (statsQuery.data?.input_tokens ?? 0) + (statsQuery.data?.output_tokens ?? 0);
  const errorRate  = reqCount ? (failed / reqCount * 100).toFixed(2) : "0.00";
  const avgDurMs   = usageQuery.data?.totals?.average_duration_ms;

  const modelOptions    = useMemo(() => (directory.data?.items ?? []).map((item) => ({ label: item.model_name, value: item.model_name })), [directory.data]);
  const protocolOptions = useMemo(() => Array.from(new Set((directory.data?.items ?? []).flatMap((item) => item.protocols))).sort().map((value) => ({ label: value, value })), [directory.data]);

  const periodLabel = isChinese ? `最近 ${rangeDays === 1 ? "24 小时" : `${rangeDays} 天`}` : `Last ${rangeDays === 1 ? "24h" : `${rangeDays}d`}`;
  const resetFilters = () => { setRequestIdInput(""); setModel(""); setProtocol(""); setStatus(""); setOffset(0); };
  const failure      = reqQuery.error ?? statsQuery.error ?? usageQuery.error;

  if (failure) {
    return (
      <Result
        status="error"
        title={isChinese ? "请求记录暂不可用" : "Request history unavailable"}
        subTitle={failure instanceof Error ? failure.message : String(failure)}
        extra={<Button type="primary" onClick={() => { void reqQuery.refetch(); void statsQuery.refetch(); void usageQuery.refetch(); }}>{isChinese ? "重试" : "Retry"}</Button>}
      />
    );
  }

  const currency = organization?.default_currency ?? "CNY";

  const panelContent = selected ? (
    <div className="usage-panel">
      <div className="usage-panel__top">
        <span className="usage-panel__title">{isChinese ? "请求详情" : "Request details"}</span>
        <Button type="text" shape="circle" icon={<CloseOutlined />}
          aria-label={isChinese ? "关闭" : "Close"} onClick={() => setSelected(undefined)} />
      </div>
      <RequestSummaryHead req={selected} locale={locale} />
      <RequestDetailsBody req={selected} locale={locale} isChinese={isChinese} currency={currency} />
    </div>
  ) : null;

  const columns: TableColumnsType<RelayRequest> = [
    {
      title: isChinese ? "时间" : "Timestamp", key: "ts", width: 178,
      render: (_, row) => <span className="usage-table__timestamp">{formatDate(row.started_at, locale)}</span>,
    },
    {
      title: isChinese ? "请求 ID" : "Request ID", key: "id", width: 190,
      render: (_, row) => (
        <Typography.Text className="usage-table__id" copyable={{ text: row.request_id, icon: <CopyOutlined /> }}>
          {row.request_id}
        </Typography.Text>
      ),
    },
    {
      title: isChinese ? "状态" : "Status", key: "status", width: 160,
      render: (_, row) => <StatusPill status={row.status} locale={locale} />,
    },
    {
      title: isChinese ? "模型" : "Model", key: "model", width: 170,
      render: (_, row) => row.model || "—",
    },
    {
      title: "Tokens", key: "tokens", align: "right", width: 105,
      render: (_, row) => <span className="usage-table__num">{formatNumber((row.input_tokens ?? 0) + (row.output_tokens ?? 0), locale)}</span>,
    },
    {
      title: isChinese ? "耗时" : "Duration", key: "duration", align: "right", width: 105,
      render: (_, row) => <span className="usage-table__num">{fmtDuration(durationMs(row))}</span>,
    },
    {
      title: isChinese ? "操作" : "Actions", key: "actions", align: "right", width: 72,
      render: (_, row) => (
        <Tooltip title={isChinese ? "查看详情" : "View details"}>
          <Button type="text" size="small"
            aria-label={isChinese ? "查看详情" : "View details"}
            onClick={(e) => { e.stopPropagation(); setSelected(row); }}>···</Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <section className="portal-page-grid">
      <PageHeader
        eyebrow={`${organization?.display_name ?? "LonMax AI"} / ${project?.display_name ?? ""}`}
        title={isChinese ? "请求与用量" : "Requests & usage"}
        description={isChinese ? "监控当前时间范围内的 API 性能与资源消耗，用请求 ID 定位每一次调用。" : "Monitor API performance and consumption for the selected range; trace any call by request ID."}
        actions={
          <Segmented
            label={isChinese ? "时间范围" : "Time range"}
            value={String(rangeDays)}
            options={[
              { value: "1", label: "24h" },
              { value: "7", label: isChinese ? "7 天" : "7d" },
              { value: "30", label: isChinese ? "30 天" : "30d" },
            ]}
            onChange={(v) => { setOffset(0); setRangeDays(Number(v) as 1 | 7 | 30); }}
          />
        }
      />

      {/* ── Metric cards (aggregates for the whole range) ── */}
      <div className="portal-metrics">
        <MetricCard
          tone="brand"
          label={isChinese ? "请求数" : "Requests"}
          value={formatNumber(reqCount, locale)}
          detail={periodLabel}
        />
        <MetricCard
          label={isChinese ? "Token 使用量" : "Token usage"}
          value={formatNumber(tokenUsage, locale, { notation: "compact" })}
          detail={periodLabel}
        />
        <MetricCard
          label={isChinese ? "平均耗时" : "Average duration"}
          value={avgDurMs === undefined ? "—" : fmtDuration(avgDurMs)}
          detail={periodLabel}
        />
        <MetricCard
          tone={Number(errorRate) > 0 ? "warning" : "success"}
          label={isChinese ? "错误率" : "Error rate"}
          value={`${errorRate}%`}
          detail={isChinese ? `${formatNumber(failed, locale)} 次失败 / ${formatNumber(reqCount, locale)} 次` : `${formatNumber(failed, locale)} failed / ${formatNumber(reqCount, locale)}`}
        />
      </div>

      {/* ── Filter bar ── */}
      <Card className="usage-filters" variant="outlined">
        <Input className="usage-filters__search" prefix={<SearchOutlined />} value={requestIdInput} placeholder={isChinese ? "搜索 asrq_ 请求 ID" : "Search Request ID (e.g. asrq_...)"} allowClear onChange={(e) => { setOffset(0); setRequestIdInput(e.target.value); }} />
        <Select className="usage-filters__select" allowClear showSearch value={model || undefined} placeholder={isChinese ? "全部模型" : "All models"} options={modelOptions} optionFilterProp="label" onChange={(v) => { setOffset(0); setModel(v ?? ""); }} />
        <Select className="usage-filters__select" allowClear value={protocol || undefined} placeholder={isChinese ? "全部协议" : "All protocols"} options={protocolOptions} onChange={(v) => { setOffset(0); setProtocol(v ?? ""); }} />
        <Select className="usage-filters__select" allowClear value={status || undefined} placeholder={isChinese ? "全部状态" : "All statuses"} options={[{ label: isChinese ? "成功" : "Succeeded", value: "succeeded" }, { label: isChinese ? "失败" : "Failed", value: "failed" }, { label: isChinese ? "待对账" : "Pending reconciliation", value: "pending_reconciliation" }]} onChange={(v) => { setOffset(0); setStatus(v ?? ""); }} />
        <Button className="usage-filters__reset" icon={<ReloadOutlined />} onClick={resetFilters}>{isChinese ? "重置" : "Reset"}</Button>
      </Card>

      {/* ── Requests table ── */}
      <Card className="usage-table" variant="outlined" title={isChinese ? "最近请求" : "Recent requests"}>
        <Table
          rowKey="request_id"
          loading={reqQuery.isLoading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 970 }}
          size="middle"
          onRow={(record) => ({ onClick: () => setSelected(record) })}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={isChinese ? "未匹配到请求" : "No matching requests"} /> }}
          pagination={{ current: Math.floor(offset / 50) + 1, pageSize: 50, total: totalCount, showSizeChanger: false, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`, onChange: (page) => setOffset((page - 1) * 50) }}
        />
      </Card>

      {/* Inline panel on wide screens */}
      {selected && showPanel && <DetailsPanel>{panelContent}</DetailsPanel>}

      {/* Drawer on narrow screens — same summary head + body as the panel */}
      {!showPanel && (
        <Drawer
          className="usage-panel-drawer"
          placement="right" width={380}
          open={Boolean(selected)} closable
          onClose={() => setSelected(undefined)}
          title={isChinese ? "请求详情" : "Request details"}
        >
          {selected && (
            <>
              <RequestSummaryHead req={selected} locale={locale} />
              <RequestDetailsBody req={selected} locale={locale} isChinese={isChinese} currency={currency} />
            </>
          )}
        </Drawer>
      )}
    </section>
  );
}

export function UsagePage() {
  return <WorkspaceGuard><UsagePageInner /></WorkspaceGuard>;
}
